import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CodeIcon from '@mui/icons-material/Code';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import StorageIcon from '@mui/icons-material/Storage';
import { Prompt } from '../../types/chat';

// ============================================================================
// Interfaces
// ============================================================================
interface PromptSelectorProps {
  selectedPrompt?: Prompt | null;
  availablePrompts: Prompt[];
  onPromptChange: (prompt: Prompt) => void;
  disabled?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================
const getPromptIcon = (actionType: string) => {
  switch (actionType) {
    case 'chat_coding':
      return <CodeIcon fontSize="small" />;
    case 'chat_support':
      return <SupportAgentIcon fontSize="small" />;
    case 'chat_project':
      return <BusinessCenterIcon fontSize="small" />;
    case 'chat_database':
      return <StorageIcon fontSize="small" />;
    case 'chat_general':
    default:
      return <SmartToyIcon fontSize="small" />;
  }
};

const getModelChipColor = (model: string) => {
  return model.includes('gpt-4') ? 'primary' : 'secondary';
};

// ============================================================================
// Component
// ============================================================================
const PromptSelector: React.FC<PromptSelectorProps> = ({
  selectedPrompt,
  availablePrompts,
  onPromptChange,
  disabled = false
}) => {
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<number>) => {
    const promptId = event.target.value as number;
    const prompt = availablePrompts.find(p => p.id === promptId);
    if (prompt) {
      onPromptChange(prompt);
    }
  };

  return (
    <Box sx={{ mb: 2, minWidth: 250 }}>
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel id="prompt-selector-label">AI Assistant</InputLabel>
        <Select
          labelId="prompt-selector-label"
          id="prompt-selector"
          value={selectedPrompt?.id || ''}
          label="AI Assistant"
          onChange={handleChange}
          renderValue={(value) => {
            const prompt = availablePrompts.find(p => p.id === value);
            if (!prompt) return '';
            
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getPromptIcon(prompt.action_type)}
                <Typography variant="body2">{prompt.title}</Typography>
                <Chip
                  label={prompt.model}
                  size="small"
                  color={getModelChipColor(prompt.model)}
                  variant="outlined"
                  sx={{ ml: 'auto', height: 20, fontSize: '0.75rem' }}
                />
              </Box>
            );
          }}
        >
          {availablePrompts.map((prompt) => (
            <MenuItem key={prompt.id} value={prompt.id}>
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {prompt.title}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {prompt.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip 
                        label={prompt.model} 
                        size="small" 
                        color={getModelChipColor(prompt.model)}
                      />
                      <Chip 
                        label={`Temp: ${prompt.temperature}`} 
                        size="small" 
                        variant="outlined"
                      />
                      <Chip 
                        label={`${prompt.max_tokens} tokens`} 
                        size="small" 
                        variant="outlined"
                      />
                      {prompt.action_type === 'chat_database' && (
                        <Chip 
                          label="Database AI" 
                          size="small" 
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                }
                arrow
                placement="right"
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  width: '100%',
                  py: 0.5
                }}>
                  {getPromptIcon(prompt.action_type)}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {prompt.title}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block', lineHeight: 1.2 }}
                    >
                      {prompt.description}
                    </Typography>
                  </Box>
                  <Chip
                    label={prompt.model}
                    size="small"
                    color={getModelChipColor(prompt.model)}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
              </Tooltip>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default PromptSelector; 